import axios from 'axios';
import { GET_ORG_DETAILS_PUBLIC, GET_PUBLIC_TRACKING, LTL_MASTER_DATA } from 'src/api/routes';
import { getErrorMessage, getURLConfigs } from 'src/api/utils';
import { getDeploymentEnvironment } from 'src/utils/utils';
import { BUSINESS_BOOKING_API_URL, LTL_BASE_URL } from 'src/globals';
import { COURIER_PARTNER_MASTER_DATA_KEY, SEARCH_BY_TO_API_MAP } from './GlobalTrack.constants';
import {
  CourierPartnerOption,
  OrgTrackConfig,
  PublicTrackingRequestBody,
  RawOrgDetails,
  TrackingConsignment,
  TrackSearchPayload,
} from './GlobalTrack.types';

// The tracking page is public. It must not inherit the shared apiProvider's 401 interceptor,
// which force-redirects to /login — a public visitor whose lookup 401s must stay on the page
// (mirrors the source app, whose provider never navigates on 401). This bare instance keeps the
// green-deployment routing header but has no response interceptor.
const publicClient = axios.create();
publicClient.interceptors.request.use((config) => {
  const isShipsyHost = /\.shipsy\.(in|io)\/.*$/.test(config.url || '');
  if (isShipsyHost && config.headers) {
    config.headers['x-forwarded-for-green'] = getDeploymentEnvironment() === 'green';
  }
  return config;
});

const publicGet = async (url: string, headers: Record<string, unknown>) => {
  try {
    const response = await publicClient.get(url, { headers });
    return {
      isSuccess: true,
      data: response?.data?.data || response?.data,
      status: response.status,
      response: response?.data,
    };
  } catch (err: any) {
    return { isSuccess: false, errorMessage: getErrorMessage(err), status: err?.response?.status };
  }
};

const publicPost = async (url: string, body: unknown, headers: Record<string, unknown>) => {
  try {
    const response = await publicClient.post(url, body, { headers });
    return {
      isSuccess: true,
      data: response?.data?.data || response?.data,
      response: response?.data,
    };
  } catch (err: any) {
    return { isSuccess: false, errorMessage: getErrorMessage(err), status: err?.response?.status };
  }
};

export const extractTrackingConsignments = (data: unknown): TrackingConsignment[] => {
  if (Array.isArray(data)) {
    return data as TrackingConsignment[];
  }
  const list = (data as { data?: TrackingConsignment[]; consignments?: TrackingConsignment[] })
    ?.data;
  return Array.isArray(list) ? list : [];
};

export const fetchPublicTracking = async (payload: TrackSearchPayload, captchaToken?: string) => {
  const { organisationPrettyName, origin } = getURLConfigs();

  const body: PublicTrackingRequestBody = {
    numbers: payload.numbers,
    searchMeta: {
      searchBy: SEARCH_BY_TO_API_MAP[payload.searchBy],
      ...(payload.code ? { code: payload.code } : {}),
      ...(payload.courierPartner ? { courierPartner: payload.courierPartner } : {}),
    },
    ...(captchaToken ? { captcha: captchaToken } : {}),
  };

  return publicPost(`${BUSINESS_BOOKING_API_URL}${GET_PUBLIC_TRACKING}`, body, {
    'organisation-pretty-name': organisationPrettyName,
    'organisation-url': origin,
  });
};

export const fetchOrgCourierPartners = async () => {
  const { organisationPrettyName } = getURLConfigs();

  return publicGet(`${LTL_BASE_URL}${LTL_MASTER_DATA}`, {
    'organisation-id': organisationPrettyName,
  });
};

export const mapCourierPartners = (data: unknown): CourierPartnerOption[] => {
  const partners = (data as Record<string, unknown>)?.[COURIER_PARTNER_MASTER_DATA_KEY];
  if (!Array.isArray(partners)) {
    return [];
  }

  return partners
    .map((partner: { id?: string; name?: string }) => ({
      label: partner.name || partner.id || '',
      value: partner.name || partner.id || '',
    }))
    .filter((option) => option.value);
};

export const fetchOrgTrackDetails = async () => {
  const { organisationPrettyName, origin } = getURLConfigs();

  return publicGet(`${BUSINESS_BOOKING_API_URL}${GET_ORG_DETAILS_PUBLIC}`, {
    'organisation-pretty-name': organisationPrettyName,
    'organisation-url': origin,
  });
};

export const mapOrgTrackConfig = (details?: RawOrgDetails | null): OrgTrackConfig => {
  const data = details || {};

  return {
    logoUrl: data.logoUrl || undefined,
    organisationName: data.organisationName || undefined,
    trackingPageBackgroundUrl: data.organisationBackgroundUrl || undefined,
    hideLogo: Boolean(data.hideLogoOnPublicTrackpage),
    hidePoweredByShipsy: Boolean(data.hidePoweredByShipsyOnPublicTrackpage),
    hideSearchByPartnerNumber: Boolean(data.hideSearchByPartnerNumber),
    hideSearchByCustomerReferenceNumber: Boolean(data.hideSearchByCustomerReferenceNumber),
  };
};
